import { useState } from 'react'
import { companiesApi } from '../api/companies'
import type { QboCompanySummary } from '../api/types'
import { Alert } from '../components/Alert'
import { BbIcon } from '../components/BbIcon'
import type { BbIconName } from '../components/BbIcon'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/States'
import { useCompanies } from '../companies/useCompanies'
import { useConnectCompany } from '../companies/useConnectCompany'
import { formatRelative } from '../lib/format'

/* Ported from "batchbooks_switch_company", replacing the old Companies page.

   The prototype draws a Time Zone column. QboCompanySummary has no such field —
   it carries country, lastSyncAt, tokenExpiresAt and status — so Country takes
   that slot rather than showing an empty column.

   Selecting a row only stages the choice; "Switch Active Context" commits it,
   which matches the prototype and avoids switching the whole app out from under
   someone who mis-clicks.

   Disconnect is a two-step act: it marks the company offline (keeps the row, its
   job history and credit ledger) rather than deleting it, so the confirm dialog
   says what actually happens. The "Show disconnected" toggle hides the greyed-
   out rows by default while leaving the history intact. */

function statusLook(status: string): { cls: string; icon: BbIconName } {
  const s = status.toLowerCase()
  if (s.includes('approve') || s.includes('active') || s.includes('connect')) {
    if (s.includes('dis')) return { cls: 'bb-co-off', icon: 'x-circle' }
    return { cls: 'bb-co-ok', icon: 'check-circle' }
  }
  if (s.includes('pending') || s.includes('auth')) return { cls: 'bb-co-wait', icon: 'timer' }
  return { cls: 'bb-co-off', icon: 'x-circle' }
}

const isDisconnected = (company: QboCompanySummary) => company.status === 'Disconnected'

/** `embedded` drops the page chrome (title, intro copy, `bb-page` padding) so the
 *  Account hub can host this screen inside a tab panel. Everything else — hooks,
 *  API calls, table, actions — is identical either way. */
export function CompaniesPage({ embedded = false }: { embedded?: boolean }) {
  const { companies, current, select, reload } = useCompanies()
  const { connect, connecting, error: connectError } = useConnectCompany()
  const [chosen, setChosen] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDisconnected, setShowDisconnected] = useState(false)
  const [pendingDisconnect, setPendingDisconnect] = useState<QboCompanySummary | null>(null)

  // The row the user has clicked, or the active context when they have not
  // clicked one. Derived during render rather than seeded into state: a
  // disconnect moves `current` on to another company (or to none), and a stored
  // copy would keep pointing at the row that just went offline — leaving "Switch
  // Active Context" enabled for a company that can no longer run anything.
  const usable =
    chosen && companies.some((c) => c.id === chosen && !isDisconnected(c)) ? chosen : null
  const picked = usable ?? current?.id ?? null

  const disconnect = async (id: string) => {
    setBusy(id)
    setError(null)
    try {
      await companiesApi.disconnect(id)
      reload()
      setPendingDisconnect(null)
    } catch {
      setError('Could not disconnect that company.')
    } finally {
      setBusy(null)
    }
  }

  const changed = picked !== null && picked !== current?.id
  const visible = showDisconnected ? companies : companies.filter((c) => !isDisconnected(c))
  const offlineCount = companies.filter(isDisconnected).length

  // Both failures land in the same banner: one hook reports a connect that would
  // not start, the other an in-page disconnect that failed.
  const shown = error ?? connectError

  const body = (
    <>
      <div className="bb-page-head">
        <div>
          {embedded ? null : (
            <>
              <h1>Switch Company</h1>
              <p>Please select the company you want to switch to for bulk operations.</p>
            </>
          )}
        </div>
        <button
          className="bb-btn bb-btn-green"
          type="button"
          disabled={!changed}
          onClick={() => picked && select(picked)}
        >
          Switch Active Context
        </button>
      </div>

      {shown ? (
        <Alert tone="err" title="Something went wrong">
          {shown}
        </Alert>
      ) : null}

      <div className="bb-company-table-head">
        <span className="bb-check-row">
          <input
            className="bb-check"
            id="showDisconnected"
            type="checkbox"
            checked={showDisconnected}
            onChange={(event) => setShowDisconnected(event.target.checked)}
          />
          <label className="bb-check-label" htmlFor="showDisconnected">
            Show disconnected companies {offlineCount > 0 ? `(${offlineCount})` : ''}
          </label>
        </span>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon="filter"
          title="No companies to show"
          body={
            offlineCount > 0
              ? 'All connected companies have been disconnected. Turn on "Show disconnected companies" to see them, or connect a new one.'
              : 'Connect a company to start importing, exporting and cleaning up its data.'
          }
          action={
            <button className="bb-btn bb-btn-green" type="button" disabled={connecting} onClick={() => void connect()}>
              {connecting ? 'Opening QuickBooks…' : 'Connect a company'}
            </button>
          }
        />
      ) : (
        <div className="bb-activity">
          <table className="bb-rtable">
            <thead>
              <tr>
                <th className="bb-radio-cell">
                  <span className="sr-only">Active</span>
                </th>
                <th>Company Name</th>
                <th>Last Connected Time</th>
                <th>Realm ID</th>
                <th>Country</th>
                <th>Status</th>
                <th className="bb-a-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((company) => {
                const look = statusLook(company.status)
                const off = isDisconnected(company)
                const on = picked === company.id
                return (
                  <tr
                    className={on ? 'bb-pickrow bb-pickrow-on' : 'bb-pickrow'}
                    key={company.id}
                    onClick={() => setChosen(company.id)}
                  >
                    <td className="bb-radio-cell">
                      <input
                        type="radio"
                        name="activeCompany"
                        aria-label={`Select ${company.companyName}`}
                        checked={on}
                        disabled={off}
                        onChange={() => setChosen(company.id)}
                      />
                    </td>
                    <td>
                      <b>{company.companyName}</b>
                    </td>
                    <td>{company.lastSyncAt ? formatRelative(company.lastSyncAt) : '—'}</td>
                    <td>{company.realmId}</td>
                    <td>{company.country ?? '—'}</td>
                    <td>
                      <span className={`bb-co-status ${look.cls}`}>
                        <BbIcon name={look.icon} />
                        {company.status}
                      </span>
                    </td>
                    <td className="bb-a-right">
                      <button
                        className={off ? 'bb-alink bb-alink-quiet' : 'bb-alink bb-alink-warn'}
                        type="button"
                        disabled={busy === company.id}
                        onClick={(event) => {
                          event.stopPropagation()
                          if (off) return
                          setPendingDisconnect(company)
                        }}
                      >
                        {busy === company.id ? 'Disconnecting…' : 'Disconnect'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className="bb-tablefoot">
            <span>
              Active context: <b>{current?.companyName ?? 'none'}</b>
            </span>
            {!showDisconnected && offlineCount > 0 ? (
              <span className="bb-map-help">
                {offlineCount} disconnected {offlineCount === 1 ? 'company' : 'companies'} hidden
              </span>
            ) : null}
            <span className="spacer" />
            <button className="bb-btn bb-btn-quiet" type="button" disabled={connecting} onClick={() => void connect()}>
              <BbIcon name="plus" />
              {connecting ? 'Opening QuickBooks…' : 'Connect another company'}
            </button>
          </div>
        </div>
      )}

      <Modal
        open={pendingDisconnect !== null}
        title={`Disconnect ${pendingDisconnect?.companyName ?? 'this company'}?`}
        onClose={() => setPendingDisconnect(null)}
        footer={
          <>
            <button className="bb-btn bb-btn-quiet" type="button" onClick={() => setPendingDisconnect(null)}>
              Cancel
            </button>
            <span className="spacer" />
            <button
              className="bb-btn bb-btn-danger"
              type="button"
              disabled={busy === pendingDisconnect?.id}
              onClick={() => pendingDisconnect && void disconnect(pendingDisconnect.id)}
            >
              {busy === pendingDisconnect?.id ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        }
      >
        <div className="bb-danger" style={{ padding: '8px 8px 24px', textAlign: 'left' }}>
          <p style={{ margin: 0 }}>
            <b>{pendingDisconnect?.companyName ?? 'This company'}</b> will be marked{' '}
            <b>Disconnected</b>. It will stop running imports and exports until it is
            reconnected.
          </p>
          <p style={{ margin: '12px 0 0' }}>
            Its row, job history and credit usage are <b>kept</b> — this is not a permanent
            removal. Reconnecting the same company later restores it with its history intact.
          </p>
        </div>
      </Modal>
    </>
  )

  return embedded ? body : <div className="bb bb-page bb-page-wide">{body}</div>
}
